/**
 * Turns the text a user pastes into the rows the bulk search API takes.
 *
 * Separators, decided once here so the dialog and the API agree:
 *  - rows are always separated by a new line;
 *  - "one column" mode: one value per line. Text fields (name, company, address, city,
 *    MLS) also split on ";" and never on commas, because "Smith, John" and
 *    "Main St, Suite 4" contain them. Code-like fields (email, phone, member id, state,
 *    ZIP) also split on commas and ";", and emails on spaces;
 *  - "several columns" mode: columns are separated by a TAB (what Excel and Google Sheets
 *    produce when you copy cells) or by "|" or ";". A comma separates columns only when
 *    the first line is a header such as "Name,Email,Company".
 */
export const BULK_SEARCH_MAX_ROWS = 500

const BY_LINE = "line"
const BY_SEPARATOR = "separator"

export const BULK_SEARCH_FIELDS = [
  {
    value: "name",
    label: "Name",
    split: BY_LINE,
    aliases: ["name", "full name", "agent", "agent name", "person", "contact"],
  },
  {
    value: "email",
    label: "Email",
    split: BY_SEPARATOR,
    aliases: ["email", "e-mail", "email address", "mail"],
  },
  {
    value: "phone",
    label: "Phone",
    split: BY_SEPARATOR,
    aliases: [
      "phone",
      "phone number",
      "mobile",
      "cell",
      "cell phone",
      "telephone",
      "tel",
    ],
  },
  {
    value: "member_id",
    label: "MLS member ID",
    split: BY_SEPARATOR,
    aliases: [
      "member id",
      "mls member id",
      "agent id",
      "agent mls id",
      "member mls id",
    ],
  },
  {
    value: "company",
    label: "Company",
    split: BY_LINE,
    aliases: [
      "company",
      "office",
      "office name",
      "brokerage",
      "firm",
      "broker",
    ],
  },
  {
    value: "address",
    label: "Address",
    split: BY_LINE,
    aliases: ["address", "street", "street address"],
  },
  {
    value: "city",
    label: "City",
    split: BY_LINE,
    aliases: ["city", "town"],
  },
  {
    value: "state",
    label: "State",
    split: BY_SEPARATOR,
    aliases: ["state", "province", "st"],
  },
  {
    value: "zipcode",
    label: "ZIP / postal code",
    split: BY_SEPARATOR,
    aliases: [
      "zip",
      "zip code",
      "zipcode",
      "postal code",
      "postcode",
      "postal",
    ],
  },
  {
    value: "mls",
    label: "MLS",
    split: BY_LINE,
    aliases: ["mls", "mls name", "mls id", "board", "association"],
  },
]

// Demo values shown as the placeholder of the paste box, so users see the format.
const EXAMPLE_VALUES = {
  name: ["Jane Doe", "Smith, John A.", "Pat Lee"],
  email: ["jane.doe@example.com", "john.smith@example.com", "pat@example.com"],
  phone: ["(864) 555-0100", "+1 864 555 0101", "864.555.0102"],
  member_id: ["12345", "67890", "24680"],
  company: ["Keller Williams", "RE/MAX Greenville", "Coldwell Banker"],
  address: ["123 N Main St", "45 Elm Ave, Suite 2", "9 Oak Road"],
  city: ["Greenville", "Columbia", "St. Petersburg"],
  state: ["SC", "GA", "North Carolina"],
  zipcode: ["29601", "29201-1234", "K1A 0B1"],
  mls: ["Greenville SC (Greater Greenville AOR)"],
}

/** Placeholder for "One column" mode: three demo values of the chosen field. */
export function singleColumnPlaceholder(fieldValue) {
  const examples = EXAMPLE_VALUES[fieldValue] || EXAMPLE_VALUES.name
  return `Paste one value per line, for example:\n${examples.join("\n")}`
}

/** Placeholder for "Several columns" mode: a header, then one person per line. */
export const TABLE_PLACEHOLDER = [
  "Paste rows here, one person per line, columns separated by a tab or |. For example:",
  "Name | Email | Company",
  "Jane Doe | jane.doe@example.com | Keller Williams",
  "John Smith |  | RE/MAX Greenville",
  "Pat Lee | pat@example.com | ",
].join("\n")

const FIELD_BY_VALUE = Object.fromEntries(
  BULK_SEARCH_FIELDS.map((field) => [field.value, field])
)
const FIELD_BY_ALIAS = Object.fromEntries(
  BULK_SEARCH_FIELDS.flatMap((field) =>
    field.aliases.map((alias) => [normalizeHeader(alias), field.value])
  )
)
// Order tried for text columns when there is no header to say what they are.
const TEXT_COLUMN_GUESS_ORDER = ["name", "company", "address", "city", "mls"]
const TABLE_DELIMITERS = ["\t", "|", ";"]

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/
const PHONE_PATTERN = /^\+?[\d\s().-]{10,}$/
const ZIP_PATTERN = /^\d{5}(-\d{4})?$/
const STATE_PATTERN = /^[A-Za-z]{2}$/

function normalizeHeader(text) {
  return text
    .toLowerCase()
    .replace(/[_\-.]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

export function fieldForHeader(text) {
  return FIELD_BY_ALIAS[normalizeHeader(text || "")] || null
}

function digitCount(text) {
  return text.replace(/\D/g, "").length
}

/**
 * A first row is a header when most of its cells are known column names and none of them
 * looks like data. `strict` (used before commas count as separators) needs all of them to
 * be known names, so "Keller Williams, Inc" is never mistaken for a header.
 */
function looksLikeHeader(cells, { strict = false } = {}) {
  const filled = cells.filter((cell) => cell.trim() !== "")
  if (!filled.length) {
    return false
  }
  const recognized = filled.filter((cell) => fieldForHeader(cell)).length
  const looksLikeData = filled.some(
    (cell) => EMAIL_PATTERN.test(cell) || digitCount(cell) >= 7
  )
  if (looksLikeData || recognized === 0) {
    return false
  }
  return strict ? recognized === filled.length : recognized * 2 >= filled.length
}

function sniffField(values) {
  const sample = values.filter((value) => value !== "").slice(0, 20)
  if (!sample.length) {
    return null
  }
  const mostly = (test) => sample.filter(test).length / sample.length >= 0.8
  if (mostly((v) => EMAIL_PATTERN.test(v))) return "email"
  if (
    mostly((v) => PHONE_PATTERN.test(v) && [10, 11].includes(digitCount(v)))
  ) {
    return "phone"
  }
  if (mostly((v) => ZIP_PATTERN.test(v))) return "zipcode"
  if (mostly((v) => STATE_PATTERN.test(v))) return "state"
  return null
}

function dedupe(values) {
  const seen = new Set()
  const unique = []
  values.forEach((value) => {
    const key = value.toLowerCase()
    if (!seen.has(key)) {
      seen.add(key)
      unique.push(value)
    }
  })
  return { values: unique, duplicates: values.length - unique.length }
}

/** "One column" mode: the values of one field, one per line. */
export function parseSingleColumn(text, fieldValue) {
  const field = FIELD_BY_VALUE[fieldValue]
  let pattern = /[\r\n;]+/
  if (field && field.split === BY_SEPARATOR) {
    pattern = fieldValue === "email" ? /[\r\n;,\s]+/ : /[\r\n;,]+/
  }
  const raw = (text || "")
    .split(pattern)
    .map((value) => value.trim())
    .filter(Boolean)
  const { values, duplicates } = dedupe(raw)
  return {
    rows: values.map((value) => ({ [fieldValue]: value })),
    duplicates,
  }
}

/**
 * "Several columns" mode. Returns the detected columns (each with the field it holds,
 * or null when it is ignored), the body rows as arrays of text, and what was detected.
 */
export function parseTable(text) {
  const lines = (text || "").split(/\r?\n/).filter((line) => line.trim() !== "")
  if (!lines.length) {
    return { columns: [], body: [], hasHeader: false, delimiter: null }
  }
  let delimiter =
    TABLE_DELIMITERS.find((candidate) =>
      lines.some((line) => line.includes(candidate))
    ) || null
  if (!delimiter && looksLikeHeader(lines[0].split(","), { strict: true })) {
    delimiter = ","
  }
  const cells = lines.map((line) =>
    delimiter ? line.split(delimiter).map((cell) => cell.trim()) : [line.trim()]
  )
  const hasHeader = looksLikeHeader(cells[0])
  const header = hasHeader ? cells[0] : null
  const body = hasHeader ? cells.slice(1) : cells
  const width = Math.max(...cells.map((row) => row.length))
  const used = new Set()
  const textGuesses = [...TEXT_COLUMN_GUESS_ORDER]
  const columns = Array.from({ length: width }, (_, index) => {
    let field = header
      ? fieldForHeader(header[index])
      : sniffField(body.map((row) => row[index] || ""))
    if (!header && !field) {
      field = textGuesses.find((candidate) => !used.has(candidate)) || null
    }
    if (field && used.has(field)) {
      field = null
    }
    if (field) {
      used.add(field)
    }
    return { index, field, header: header ? header[index] || "" : "" }
  })
  return { columns, body, hasHeader, delimiter }
}

/** The API rows for the body of a table, using the (possibly edited) column mapping. */
export function buildTableRows(columns, body) {
  return body
    .map((cells) => {
      const row = {}
      columns.forEach(({ index, field }) => {
        const value = (cells[index] || "").trim()
        if (field && value) {
          row[field] = value
        }
      })
      return row
    })
    .filter((row) => Object.keys(row).length > 0)
}

/** An error to show instead of searching, or null when the rows can be sent. */
export function validateBulkRows(rows) {
  if (!rows.length) {
    return "Paste at least one row."
  }
  if (rows.length > BULK_SEARCH_MAX_ROWS) {
    return `You can search up to ${BULK_SEARCH_MAX_ROWS} rows at once. You pasted ${rows.length}.`
  }
  const onlyMls = rows.findIndex(
    (row) => Object.keys(row).length === 1 && row.mls
  )
  if (onlyMls >= 0) {
    return `Row ${
      onlyMls + 1
    } has only an MLS. MLS narrows another column, so add a name, email or similar.`
  }
  return null
}
